import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPageById } from '../services/firestoreService';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { Helmet } from 'react-helmet-async';
import '../styles/pages/PublicPage.scss';

// ヘッドコンテンツをパースする関数
const parseHeadContent = (headContent) => {
    if (!headContent) return [];



    // <head>タグの中身を抽出（headタグがない場合はそのまま使用）
    let content = headContent;
    if (headContent.includes('<head>') && headContent.includes('</head>')) {
        content = headContent.split('</head>')[0].split('<head>')[1] || headContent;
    }



    // 各タグを抽出
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<html><head>${content}</head></html>`, 'text/html');
    const elements = Array.from(doc.head.children);



    return elements.map((element, index) => {
        const tagName = element.tagName.toLowerCase();
        const attributes = Array.from(element.attributes).reduce((acc, attr) => {
            acc[attr.name] = attr.value;
            return acc;
        }, {});

        // scriptタグの場合
        if (tagName === 'script') {
            return {
                type: 'script',
                props: {
                    ...attributes,
                    key: index,
                    dangerouslySetInnerHTML: { __html: element.innerHTML }
                }
            };
        }

        // metaタグの場合
        if (tagName === 'meta') {
            return {
                type: 'meta',
                props: { ...attributes, key: index }
            };
        }

        // linkタグの場合
        if (tagName === 'link') {
            return {
                type: 'link',
                props: { ...attributes, key: index }
            };
        }

        // styleタグの場合
        if (tagName === 'style') {
            return {
                type: 'style',
                props: {
                    ...attributes,
                    key: index,
                    dangerouslySetInnerHTML: { __html: element.innerHTML }
                }
            };
        }

        return null;
    }).filter(Boolean);
};

const PreviewPage = () => {
    const { pageId } = useParams();
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [headElements, setHeadElements] = useState([]);
    const [bodyContent, setBodyContent] = useState('');
    const { user } = useAuth();

    useEffect(() => {
        const fetchPage = async () => {
            if (!pageId) return;

            // ログイン状態を確認
            console.log('🔐 プレビューページ - ログイン状態:', {
                currentUser: user?.currentUser,
                accountId: user?.accountId,
                loading: user?.loading
            });

            try {
                const fetchedPage = await getPageById(pageId);
                if (fetchedPage) {
                    setPage(fetchedPage);

                    // HTML全体からheadとbodyの内容を抽出
                    let headContent = '';
                    let extractedBodyContent = '';

                    if (fetchedPage.html) {
                        // headタグの内容を抽出
                        const headMatch = fetchedPage.html.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
                        if (headMatch) {
                            headContent = headMatch[1].trim();
                        }

                        // bodyタグの内容を抽出
                        const bodyMatch = fetchedPage.html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
                        if (bodyMatch) {
                            extractedBodyContent = bodyMatch[1].trim();
                        }
                    }

                    // headコンテンツをパース
                    if (headContent) {
                        const parsedElements = parseHeadContent(headContent);
                        setHeadElements(parsedElements);
                    }

                    // bodyコンテンツを設定
                    setBodyContent(extractedBodyContent);
                } else {
                    setError('ページが見つかりませんでした。');
                }
            } catch (err) {
                console.error('ページの取得に失敗しました:', err);
                console.error('エラー詳細:', {
                    code: err.code,
                    message: err.message,
                    stack: err.stack
                });

                // 権限エラーの場合は詳細情報を出力
                if (err.code === 'permission-denied') {
                    console.error('🚫 権限不足エラー - pageId:', pageId);
                    console.error('🔐 認証状態:', 'ログインが必要です');
                }

                setError('ページの読み込み中にエラーが発生しました。');
            } finally {
                setLoading(false);
            }
        };

        fetchPage();
    }, [pageId]);

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error || !page) {
        return (
            <div className="error-container">
                <p className="error-message">{error || 'ページが見つかりませんでした。'}</p>
            </div>
        );
    }

    // デバッグ用のログ


    return (
        <>
            <Helmet>
                <title>{page.title} - プレビュー</title>
                {headElements.map(element => {
                    const Component = element.type;
                    return <Component {...element.props} />;
                })}
                {page.jsonld && (
                    <script type="application/ld+json">
                        {page.jsonld}
                    </script>
                )}
            </Helmet>
            <div className="preview-banner">
                プレビューモード
            </div>
            <div className="public-page-container preview-mode">
                <div dangerouslySetInnerHTML={{ __html: bodyContent }} />
            </div>
        </>
    );
};

export default PreviewPage; 